import React, { useEffect, useMemo, useState } from 'react';
import { Appointment, Company, CompanyCustomerSummary, AddOn } from '@/types';
import { api } from '@/services/api';
import { Pagination } from '@shared/components/common/Pagination';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { formatUkDate } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { buildMapById } from '@/lib/collections';
import { Calendar, Search, X, Loader2 } from 'lucide-react';

interface CompanyActivityPanelProps {
  company: Company;
  addOns: AddOn[];
  onClose: () => void;
}

export const CompanyActivityPanel: React.FC<CompanyActivityPanelProps> = ({
  company,
  addOns,
  onClose
}) => {
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);

  const [activityTab, setActivityTab] = useState<'bookings' | 'customers'>('bookings');

  const [activityBookings, setActivityBookings] = useState<Appointment[]>([]);
  const [activityBookingsPage, setActivityBookingsPage] = useState(1);
  const [activityBookingsPageSize, setActivityBookingsPageSize] = useState(25);
  const [activityBookingsTotalCount, setActivityBookingsTotalCount] = useState(0);
  const [isLoadingActivityBookings, setIsLoadingActivityBookings] = useState(false);
  const [activityBookingsError, setActivityBookingsError] = useState('');

  const [activityCustomers, setActivityCustomers] = useState<CompanyCustomerSummary[]>([]);
  const [activityCustomersPage, setActivityCustomersPage] = useState(1);
  const [activityCustomersPageSize, setActivityCustomersPageSize] = useState(25);
  const [activityCustomersTotalCount, setActivityCustomersTotalCount] = useState(0);
  const [isLoadingActivityCustomers, setIsLoadingActivityCustomers] = useState(false);
  const [activityCustomersError, setActivityCustomersError] = useState('');
  const [activityCustomerSearch, setActivityCustomerSearch] = useState('');

  const getAddOnNames = (booking: Appointment) =>
    (booking.addOnIds || [])
      .map((id) => addOnsById.get(id)?.name)
      .filter(Boolean)
      .join(', ');

  // Reset pagination and clear data when company changes
  useEffect(() => {
    if (company.id) {
      setActivityTab('bookings');
      setActivityBookingsPage(1);
      setActivityCustomersPage(1);
      setActivityCustomerSearch('');
      return;
    }
    setActivityBookings([]);
    setActivityBookingsTotalCount(0);
    setActivityBookingsError('');
    setActivityCustomers([]);
    setActivityCustomersTotalCount(0);
    setActivityCustomersError('');
  }, [company.id]);

  // Fetch bookings
  useEffect(() => {
    if (!company.id || activityTab !== 'bookings') return;
    let isActive = true;
    setIsLoadingActivityBookings(true);
    setActivityBookingsError('');
    api
      .listStaffBookings({
        page: activityBookingsPage,
        pageSize: activityBookingsPageSize,
        companyId: company.id,
        sortBy: 'date',
        sortDir: 'desc'
      })
      .then((data) => {
        if (!isActive) return;
        setActivityBookings(data.items || []);
        setActivityBookingsTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setActivityBookings([]);
        setActivityBookingsTotalCount(0);
        setActivityBookingsError(normalizeErrorMessage(err, 'Failed to load bookings.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingActivityBookings(false);
      });
    return () => {
      isActive = false;
    };
  }, [company.id, activityTab, activityBookingsPage, activityBookingsPageSize]);

  // Fetch customers
  useEffect(() => {
    if (!company.id || activityTab !== 'customers') return;
    let isActive = true;
    setIsLoadingActivityCustomers(true);
    setActivityCustomersError('');
    api
      .listCompanyCustomers(company.id, {
        page: activityCustomersPage,
        pageSize: activityCustomersPageSize,
        q: activityCustomerSearch || undefined,
        sortBy: 'lastVisitAt',
        sortDir: 'desc'
      })
      .then((data) => {
        if (!isActive) return;
        setActivityCustomers(data.items || []);
        setActivityCustomersTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setActivityCustomers([]);
        setActivityCustomersTotalCount(0);
        setActivityCustomersError(normalizeErrorMessage(err, 'Failed to load customers.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingActivityCustomers(false);
      });
    return () => {
      isActive = false;
    };
  }, [
    company.id,
    activityTab,
    activityCustomersPage,
    activityCustomersPageSize,
    activityCustomerSearch
  ]);

  // Reset customer page when search changes
  useEffect(() => {
    setActivityCustomersPage(1);
  }, [activityCustomerSearch]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden animate-fade-in flex flex-col max-h-[85vh]">
        <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 p-2 rounded-lg text-slate-600">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-slate-900">{company.name} Activity</h3>
              <p className="text-xs text-slate-500">
                Bookings and unique customers linked to this company
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActivityTab('bookings')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activityTab === 'bookings' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Bookings ({activityBookingsTotalCount})
              </button>
              <button
                onClick={() => setActivityTab('customers')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activityTab === 'customers' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Customers ({activityCustomersTotalCount})
              </button>
            </div>
            {activityTab === 'customers' && (
              <div className="relative">
                <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  className="pl-9 pr-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 outline-none"
                  value={activityCustomerSearch}
                  onChange={(e) => setActivityCustomerSearch(e.target.value)}
                />
              </div>
            )}
          </div>

          {activityTab === 'bookings' ? (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {isLoadingActivityBookings ? (
                <div className="p-6 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Loading bookings...
                </div>
              ) : activityBookingsError ? (
                <div className="p-6 text-center text-red-500">{activityBookingsError}</div>
              ) : activityBookings.length === 0 ? (
                <div className="p-6 text-center text-slate-500 italic">
                  No bookings found for this company.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Patient</th>
                        <th className="px-4 py-3">Service</th>
                        <th className="px-4 py-3">Add-ons</th>
                        <th className="px-4 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {activityBookings.map((booking) => (
                        <tr key={booking.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-600">
                            <div className="font-medium">{formatUkDate(booking.date, '—')}</div>
                            <div className="text-xs text-slate-400">{booking.time}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            <div className="font-medium">{booking.customerName}</div>
                            <div className="text-xs text-slate-400">{booking.customerEmail}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {booking.serviceName || booking.serviceId || '—'}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {getAddOnNames(booking) || '—'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <StatusBadge status={booking.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {isLoadingActivityCustomers ? (
                <div className="p-6 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Loading customers...
                </div>
              ) : activityCustomersError ? (
                <div className="p-6 text-center text-red-500">{activityCustomersError}</div>
              ) : activityCustomers.length === 0 ? (
                <div className="p-6 text-center text-slate-500 italic">
                  No customers found for this company.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Customer</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3 text-center">Visits</th>
                        <th className="px-4 py-3">Last Visit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {activityCustomers.map((customer) => (
                        <tr key={`${customer.email}-${customer.id || ''}`}>
                          <td className="px-4 py-3 font-medium text-slate-700">
                            {customer.name || customer.email}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{customer.email}</td>
                          <td className="px-4 py-3 text-slate-600">{customer.phone || '—'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center min-w-[40px] px-2 py-1 text-[10px] font-bold uppercase bg-slate-100 text-slate-600 rounded-full border border-slate-200">
                              {customer.visitCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {customer.lastVisitAt ? formatUkDate(customer.lastVisitAt, '—') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t bg-slate-50">
          {activityTab === 'bookings' ? (
            <Pagination
              page={activityBookingsPage}
              pageSize={activityBookingsPageSize}
              totalCount={activityBookingsTotalCount}
              onPageChange={setActivityBookingsPage}
              onPageSizeChange={(size) => {
                setActivityBookingsPageSize(size);
                setActivityBookingsPage(1);
              }}
              isLoading={isLoadingActivityBookings}
            />
          ) : (
            <Pagination
              page={activityCustomersPage}
              pageSize={activityCustomersPageSize}
              totalCount={activityCustomersTotalCount}
              onPageChange={setActivityCustomersPage}
              onPageSizeChange={(size) => {
                setActivityCustomersPageSize(size);
                setActivityCustomersPage(1);
              }}
              isLoading={isLoadingActivityCustomers}
            />
          )}
        </div>
      </div>
    </div>
  );
};
